<template>
  <div class="d-flex flex-column ga-4">
    <div
      class="text-body-medium text-grey-darken-1 mt-1"
      data-test-id="file-max-size"
    >
      {{
        i18n.t('texts.attachments_format_size_hint', {
          number: maxFileSizeMB
        })
      }}
    </div>
    <div
      v-if="modelValue.length > 0"
      class="files-container"
      data-test-id="attachment-info"
    >
      <div v-for="file in modelValue" :key="file.uploadId" class="py-3">
        <UploadedAttachment
          :attachment-type="attachmentType"
          :file="file"
          @remove-uploaded-attachment="emit('remove-file', file)"
        />
      </div>
    </div>

    <input
      ref="filesInput"
      accept="application/pdf"
      data-test-id="input-file-attachment"
      hidden
      multiple
      type="file"
      @input="onSelectFiles"
    />

    <div
      v-if="modelValue.length > 0"
      class="text-center text-body-medium"
      :class="isTotalSizeTooLarge ? 'text-red' : 'text-grey-darken-1'"
      :data-test-id="`totalfilesize-${totalFileSize}`"
    >
      {{ i18n.t('texts.total_file_size') + ': ' + totalFileSize }}
    </div>
    <div>
      <DeepButton
        :action="openSelector"
        :disabled="isTotalSizeTooLarge"
        icon="fa fa-arrow-up-from-bracket"
        :text="i18n.t('buttons.attach_files')"
        variant="outlined"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import DeepButton from '@/components/buttons/DeepButton.vue';
import { AttachmentStatus } from '@/types/enums/AttachmentStatus.ts';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { humanFileSize } from '@/composables/useUtils';
import UploadedAttachment from '@/components/items/UploadedAttachment.vue';
import type { PropType } from 'vue';
import type { Attachment, AttachmentFile } from '@/types/Attachment';
import { v4 as uuid } from 'uuid';
import { constants } from '@/constants/constants';

export interface FileWithStatus {
  file: File;
  status: AttachmentStatus;
  attachment: Attachment;
  errorId?: string;
}

const i18n = useI18n();
const filesInput = ref();

const props = defineProps({
  modelValue: {
    type: Array as PropType<AttachmentFile[]>,
    default: () => []
  },
  maxFileSizeMB: {
    type: Number,
    default: constants.ATTACHMENTS_MAX_SIZE_MB
  },
  supportedTypes: {
    type: String,
    required: true
  },
  attachmentType: {
    type: String as PropType<'attachment' | 'signee-attachment'>,
    default: undefined
  }
});

const emit = defineEmits(['update:modelValue', 'remove-file']);

const totalFileSize = computed(() => {
  let fileSize = 0;
  props.modelValue.forEach((file) => {
    fileSize += file.size || file.attachment.size;
  });
  return humanFileSize(fileSize);
});

const isTotalSizeTooLarge = computed(() => {
  const totalBytes = props.modelValue.reduce(
    (acc, f) => acc + (f.size ?? f.attachment?.size ?? 0),
    0
  );
  return Number((totalBytes / 1_000_000).toFixed(1)) > props.maxFileSizeMB;
});

async function onSelectFiles(event: Event) {
  const input = event.target as HTMLInputElement;
  const selected = Array.prototype.slice.call(input.files) as File[];

  const maxBytes = props.maxFileSizeMB * 1_000_000;

  let runningTotal = props.modelValue.reduce(
    (acc, f) => acc + (f.size ?? f.attachment?.size ?? 0),
    0
  );

  const newFiles: AttachmentFile[] = selected.map((file) => {
    const base = {
      file,
      name: file.name,
      size: file.size,
      mimeType: undefined,
      attachementId: undefined,
      uploadId: uuid()
    };

    if (file.type !== props.supportedTypes) {
      return { ...base, status: AttachmentStatus.UNSUPPORTED };
    }

    if (runningTotal + file.size > maxBytes) {
      return { ...base, status: AttachmentStatus.EXCLUDED };
    }

    runningTotal += file.size;
    return { ...base, status: AttachmentStatus.PENDING };
  });

  emit('update:modelValue', [...props.modelValue, ...newFiles]);
  filesInput.value.value = null;
}

function openSelector() {
  filesInput.value.click();
}
</script>
